import PropTypes from 'prop-types';
import { CheckCircle, Loader, AlertCircle, FileUp, FileText, Brain, Search, Sparkles } from 'lucide-react'; // Removed unused Database icon

// Component to display the status of each processing step
const ProcessingStep = ({ label, status, icon: Icon }) => {
  let statusIcon;
  let statusColor = 'text-huntd-secondary/60'; // Default for waiting
  let pulse = false;
  let bgColor = 'bg-white'; // Default background

  switch (status) {
    case 'processing':
      statusIcon = <Loader className="animate-spin h-5 w-5 text-huntd-primary" />;
      statusColor = 'text-blue-700'; // Use a blue for processing text
      bgColor = 'bg-blue-50'; // Light blue background for processing
      pulse = true;
      break;
    case 'complete':
      statusIcon = <CheckCircle className="h-5 w-5 text-huntd-success" />;
      statusColor = 'text-green-700'; // Use green for success text
      bgColor = 'bg-green-50'; // Light green background for success
      break;
    case 'error':
      statusIcon = <AlertCircle className="h-5 w-5 text-huntd-error" />;
      statusColor = 'text-red-700'; // Use red for error text
      bgColor = 'bg-red-50'; // Light red background for error
      break;
    default: // 'waiting' or unknown
      statusIcon = <Icon className="h-5 w-5 text-huntd-secondary/40" />;
      statusColor = 'text-huntd-secondary/60';
  }

  return (
    // Apply background color, increase padding, ensure left alignment
    <li className={`flex items-center py-3 px-4 transition-colors duration-200 ${bgColor}`}>
      <div className="flex items-center"> {/* This div handles the icon + text grouping */}
        <span className={`mr-3 ${pulse ? 'animate-pulse' : ''}`}>{statusIcon}</span>
        {/* Increased text size */}
        <span className={`text-base font-medium ${statusColor} ${pulse ? 'animate-pulse' : ''}`}>{label}</span>
      </div>
    </li>
  );
};

ProcessingStep.propTypes = {
  label: PropTypes.string.isRequired,
  status: PropTypes.oneOf(['waiting', 'processing', 'complete', 'error']).isRequired,
  icon: PropTypes.elementType.isRequired,
};


// Main display component
const ProcessingStepsDisplay = ({ fileSelected, processingSteps }) => {

  // Define all possible steps from both strategies
  const stepConfig = [
    { key: 'upload', label: 'Upload CV', icon: FileUp },
    { key: 'extract_text', label: 'Extracting Text', icon: FileText },
    // Multi-Stage Specific
    { key: 'ai_broad_extract_chunk', label: 'Initial Analysis (Chunked)', icon: Brain, strategy: 'multi' }, // Mark strategy if needed
    { key: 'ai_refine_roles', label: 'Refining Roles', icon: Sparkles, strategy: 'multi' },
    { key: 'ai_global_skills', label: 'Scanning Global Skills', icon: Search, strategy: 'multi' },
    // Single-Call Specific
    { key: 'ai_single_call_processing', label: 'Comprehensive AI Analysis', icon: Brain, strategy: 'single' },
    // Common
    { key: 'complete', label: 'Processing Complete', icon: CheckCircle }
  ];

  // Determine if processing has started (any step is not 'waiting')
  const processingStarted = Object.values(processingSteps).some(status => status !== 'waiting');

  // Only render if a file is selected OR processing has started
  if (!fileSelected && !processingStarted) {
    return null;
  }

  return (
    <div className="mt-6 border border-huntd-secondary/10 rounded-lg overflow-hidden">
       {/* Determine which set of steps to show based on received stages */}
       {/* Simple approach: Show all defined steps, inactive ones will just be 'waiting' */}
       {/* More complex: Detect strategy based on first AI stage received and filter stepConfig */}
       <ul className="divide-y divide-huntd-secondary/10">
        {stepConfig.map((step) => (
          <ProcessingStep
            key={step.key}
            label={step.label}
            status={processingSteps[step.key] || 'waiting'} // Default to waiting if key somehow missing
            icon={step.icon}
          />
        ))}
      </ul>
    </div>
  );
};

ProcessingStepsDisplay.propTypes = {
  fileSelected: PropTypes.bool.isRequired,
  processingSteps: PropTypes.object.isRequired // Accept any object shape for flexibility
};

export default ProcessingStepsDisplay;